import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost, errOf } from '../lib/api';

export interface MemberLite {
  id: string;
  member_no: string | null;
  name: string;
  company: string | null;
}
export interface RoleLite {
  code: string;
  name_zh_hk: string;
  name_zh_cn: string;
  name_en: string;
}
export interface CmMember {
  id: string;
  member_id: string;
  role_code: string;
  status: string;
  member_name: string | null;
  member_no: string | null;
}

const input: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const btnPrimary: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 8,
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)',
  fontSize: 'var(--font-size-tiny)',
  border: 'none',
  cursor: 'pointer',
};

/** 委員會成員 ＋ 邀請會員加入（由 `/admin/committees` 抽出，守住 ≤200 行） */
export default function CommitteeMembersPanel({
  committeeId,
  members,
  roles,
  roleName,
  onChanged,
}: {
  committeeId: string;
  members: MemberLite[];
  roles: RoleLite[];
  roleName: (code: string) => string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [cm, setCm] = useState<CmMember[]>([]);
  const [pick, setPick] = useState({ memberId: '', roleCode: 'member' });
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ members: CmMember[] }>(`/api/committee/committees/${committeeId}`);
    setCm(r.ok && r.data ? r.data.members : []);
  }, [committeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite() {
    if (!pick.memberId) return;
    setMsg('');
    const r = await apiPost('/api/committee/members', { committeeId, memberId: pick.memberId, roleCode: pick.roleCode });
    if (!r.ok) {
      setMsg(errOf(r.data) || '邀請失敗');
      return;
    }
    setMsg(t('committee_invited_ok'));
    setPick({ memberId: '', roleCode: 'member' });
    await load();
    onChanged();
  }

  return (
    <div className="p-3 pt-0">
      <ul className="text-sm mb-2">
        {cm.length === 0 ? (
          <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
        ) : (
          cm.map((m) => (
            <li key={m.id}>
              {m.member_name ?? m.member_id} ｜ {roleName(m.role_code)} ｜{' '}
              {m.status === 'active'
                ? t('committee_status_active')
                : m.status === 'invited'
                  ? t('committee_status_invited')
                  : m.status}
            </li>
          ))
        )}
      </ul>
      <div className="flex flex-wrap gap-2 items-center">
        <select
          style={{ ...input, width: 'auto' }}
          value={pick.memberId}
          onChange={(e) => setPick({ ...pick, memberId: e.target.value })}
        >
          <option value="">{t('committee_pick_member')}</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.member_no ? `${m.member_no} ` : ''}
              {m.name}
            </option>
          ))}
        </select>
        <select
          style={{ ...input, width: 'auto' }}
          value={pick.roleCode}
          onChange={(e) => setPick({ ...pick, roleCode: e.target.value })}
        >
          {roles.map((r) => (
            <option key={r.code} value={r.code}>
              {roleName(r.code)}
            </option>
          ))}
        </select>
        <button type="button" style={btnPrimary} onClick={() => void invite()}>
          {t('committee_invite')}
        </button>
      </div>
      {msg && (
        <p className="text-sm mt-2" style={{ color: 'var(--color-primary)' }}>
          {msg}
        </p>
      )}
    </div>
  );
}
